import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keycloak, getUserInfo } from '../auth/keycloak';
import LoadingFallback from '../components/LoadingFallback';
import { ClassroomDetail, Login } from './lazyPages';

export function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  // More defensive check
  const isAuthenticated = keycloak && keycloak.authenticated;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export function RequireRole({
  role,
  children,
}: {
  role: 'ADMIN' | 'TEACHER' | 'STUDENT';
  children: JSX.Element;
}): JSX.Element {
  const isAuthenticated = keycloak && keycloak.authenticated;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Get roles using getUserInfo logic (consistent with rest of app)
  const userInfo = getUserInfo();
  const userRoles = userInfo?.roles || [];

  // Check role access
  if (role === 'ADMIN' && !userRoles.includes('ADMIN')) {
    return <Navigate to="/" replace />;
  }
  if (role === 'TEACHER' && !userRoles.includes('TEACHER') && !userRoles.includes('ADMIN')) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export function ClassroomRouteGuard(): JSX.Element {
  const { code } = useParams();
  const userInfo = getUserInfo();
  const roles: string[] = userInfo?.roles || [];
  const isTeacherOrAdmin = roles.includes('TEACHER') || roles.includes('ADMIN');
  if (!isTeacherOrAdmin) {
    return <Navigate to={`/my-classrooms/${code}`} replace />;
  }
  return (
    <React.Suspense fallback={<LoadingFallback />}>
      <ClassroomDetail />
    </React.Suspense>
  );
}

export function RedirectIfAuthed(): JSX.Element {
  // More defensive check
  const { t } = useTranslation();
  const isAuthenticated = keycloak && keycloak.authenticated;
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return (
    <React.Suspense fallback={<div>{t('common.loading')}</div>}>
      <Login />
    </React.Suspense>
  );
}

// Redirect component for /classrooms/:code/assignments/:assignmentCode -> /assignments/:assignmentCode
export function AssignmentFromClassroomRedirect() {
  const { assignmentCode } = useParams();
  return <Navigate to={`/assignments/${assignmentCode}`} replace />;
}
